import React from "react";
import { connect } from "react-redux";
import { get } from "lodash";
import classNames from "classnames";
import { ICellRenderer, ICellRendererParams } from "ag-grid-community";
import { $, merge } from "glamor";
import { DATATEST } from "constants/integration";
import { IStore } from "reducers";
import ImageIcon from "components/icons/image";
import LinkBrokenIcon from "components/icons/link-broken-icon";
import theme, { rawTheme } from "styles/theme";
import { COLLECTION_ID_FIELD_NAME } from "helpers/server-rollup";
import { getNewResizedImageDimensions } from "../product-collection-grid-view-helper";
import { IGridContext } from "../product-collection-grid-view";

type RendererParams = Omit<ICellRendererParams, "context"> & { context: IGridContext };

interface IOwnProps extends RendererParams {
    fieldLookup: string; // target key to in the item data we're interested in. e.g. images_1
    openDetailModal?: (id: string, assortmentId?: string, nodeRowIndex?: number) => void;
    onLinkProductToItem?: (itemId) => void;
    canLinkProduct?: boolean;
}

interface TStateProps {
    imageCellWidth: number;
}

interface IState {
    fields: Record<string, any>;
}

const defaultImageDimensions = {
    width: 38,
    height: 45,
};

const getPrimaryImageUrl = (fields: Record<string, any>, fieldLookup: string) =>
    fields[fieldLookup]?.string_value || null;

const FALLBACK_FIELDS = {};

const styles = merge(
    {
        textAlign: "center",
    },
    $(" .noImageIcon", {
        width: "100% !important",
        height: "100% !important",
        color: `${theme.palette.grey} !important`,
        verticalAlign: "middle",
    }),

    $(" .brokenLink", {
        backgroundColor: theme.palette.darkGrey,
        borderRadius: "50%",
        position: "absolute",
        right: "30px !important",
        top: "70%",
        transform: "translateY(-50%)",
        height: "16px !important",
        width: "16px !important",
    }),
    $(" .chevron", {
        transition: "all .15s ease-in-out",
        padding: "0px !important",
        verticalAlign: "middle",
        width: "32px !important",
    }),
    $(" .isOpen", {
        transform: "rotate(90deg)",
    }),
    $(" .linkBrokenContainer", {
        display: "inline-block",
        height: "24px",
        width: "40px",
    }),
    $(" .viewOnly", {
        position: "absolute",
        left: "40px !important",
    }),
    $(" .toggleExpand", {
        height: "20px",
        width: "20px",
        cursor: "pointer",
    })
);

const muiStyles = {
    imageContainerSmall: {
        backgroundColor: rawTheme.palette.borderColor,
        padding: "6px",
        cursor: "pointer",
        display: "inline-block",
        position: "relative",
    },
    primaryImage: {
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "contain",
        cursor: "pointer",
        padding: "0px",
        verticalAlign: "middle",
        display: "inline-block",
        position: "relative",
    },
};

export class ImageCellRenderer extends React.PureComponent<IOwnProps & TStateProps, IState> implements ICellRenderer {
    state = {
        isExpanded: false,
        isSelected: false,
        fields: {},
        showImage: true,
    };

    componentDidMount() {
        this.updateState(this.props);
    }

    componentDidUpdate(prevProps, prevState: IState) {
        this.updateState(this.props, prevState);
    }

    refresh(props: RendererParams) {
        this.updateState(props, this.state);
        return true;
    }

    private updateState(props: RendererParams, prevState?: IState) {
        const { data } = props;
        const { fields = FALLBACK_FIELDS } = data || {};

        if (!prevState || fields !== prevState.fields) {
            this.setState({ fields });
        }
    }

    handleOpenDialog = () => {
        const { node, openDetailModal, context } = this.props;

        if (openDetailModal) {
            const { fields = FALLBACK_FIELDS } = node.data;
            const collectionId = context?.isServerRollup && fields?.[COLLECTION_ID_FIELD_NAME]?.string_value;
            openDetailModal(fields.id?.string_value, collectionId, node.rowIndex);
        }
    };

    getImageDimensions = () => {
        if (this.props.colDef?.resizable) {
            return getNewResizedImageDimensions(this.props.imageCellWidth, defaultImageDimensions);
        }
        return defaultImageDimensions;
    };

    render() {
        const { primaryImage, imageContainerSmall } = muiStyles;
        const { data, node, canLinkProduct, fieldLookup, context } = this.props;
        const { fields } = this.state;

        if (
            data === null ||
            data === undefined ||
            !node ||
            !node.data ||
            !node.data.fields ||
            (context?.isServerRollup && node.group)
        ) {
            return null;
        }
        const isSMU = !get(node, ["data", "fields", "_product_id", "string_value"]);

        const primaryImageUrl = getPrimaryImageUrl(fields, fieldLookup);
        const { width, height } = this.getImageDimensions();
        const dimensionsStyle = {
            height: `${height}px`,
            width: `${width}px`,
        };

        const imageWrapperStyles: any = primaryImageUrl
            ? {
                  ...primaryImage,
                  ...dimensionsStyle,
                  backgroundImage: `url(${primaryImageUrl}?max_dimension=${width})`,
              }
            : {
                  ...imageContainerSmall,
                  ...dimensionsStyle,
              };

        return (
            <div
                {...styles}
                className="ag-cell-wrapper rowImageCell"
                role="presentation"
                data-test={DATATEST.ASSORTMENTS.ROW_IMAGE_CELL}
            >
                <span role="presentation" className="ag-cell-value">
                    <span className="ag-cell-wrapper ag-cell-expandable ag-row-group ag-row-group-indent-0">
                        <span className="ag-group-value">
                            <div
                                style={imageWrapperStyles}
                                onClick={this.handleOpenDialog}
                                data-test={primaryImageUrl ? DATATEST.ASSORTMENTS.PDP.PRIMARY_IMAGE : undefined}
                            >
                                {!primaryImageUrl && (
                                    <ImageIcon className="noImageIcon" data-test={DATATEST.ASSORTMENTS.PDP.NO_IMAGE} />
                                )}
                            </div>
                            {isSMU && (
                                <LinkBrokenIcon
                                    className={classNames("brokenLink", { viewOnly: !canLinkProduct })}
                                    fill={theme.palette.lighterGrey}
                                />
                            )}
                        </span>
                    </span>
                </span>
            </div>
        );
    }
}

const mapStateToProps = (state: IStore): TStateProps => {
    return {
        imageCellWidth: state.collection.imageCellWidth,
    };
};
export const ImageCellContainerRenderer = connect(mapStateToProps)(ImageCellRenderer);
